/**
 * Static catalog of every tool surfaced by the Zeely-Mirror /video-ads
 * landing page and the "+ New video" → "Explore all tools" modal.
 *
 * Tab labels and per-tab card ordering follow the Zeely-mirror surface.
 * WiserStudio tool copy is conversion-focused and outcome-led. Card thumbnails
 * are self-hosted under `/zeely/explore-all/<slug>.png` or the R2 landing
 * bucket (matched against Zeely's reference thumbnails where needed).
 */

export type ZeelyToolCategory = "image-ads" | "video-ads" | "avatars" | "promotion";

export type ZeelyToolBadge = "NEW" | "RECOMMENDED" | null;

export type ZeelyToolEntry = {
  /** Stable id used by analytics + the route param `?tool=<id>`. */
  id: string;
  /** Card title shown on the landing/tool picker. */
  title: string;
  /** One-sentence outcome-focused description under the title. */
  description: string;
  /** Card thumbnail URL. */
  thumbnail: string;
  /** Optional badge displayed on the card. */
  badge: ZeelyToolBadge;
  /** Tool category — used by the modal's tab filter. */
  category: ZeelyToolCategory;
  /** In-app route the card navigates to on click. */
  route: string;
  /** Whether the card surfaces as one of the four landing-page "top tools". */
  isTopTool?: boolean;
  /** Tools that don't start with a product image (e.g. Talking avatar
   *  starts with an avatar persona; Cinematic is pure text-to-video).
   *  When true, the landing card click navigates straight to the route
   *  instead of opening the asset picker. */
  skipAssetPicker?: boolean;
  /** WiserStudio-only tools we keep reachable by route but hide from
   *  the Explore-all modal to keep strict 1:1 Zeely parity in the UI. */
  hiddenInExploreModal?: boolean;
};

import { cdnUrl } from "@/lib/utils";

import { THUMB, unsplash } from "./zeelyMirrorImageCdn";

/** Thumbnails for the Explore-all modal — Zeely-matched assets.
 *  The four landing-page top tools (UGC-style / Avatar video / Animate /
 *  Talking avatar) and Explore-all point at the Zeely product
 *  thumbnails we re-hosted under /static/landing/top-tools/ on our R2
 *  bucket so the landing row renders identically to Zeely's /video. */
const R2_TOP_TOOLS = "https://pub-56b94dd8a0c946a689522e687290e19d.r2.dev/static/landing/top-tools";
const EXPLORE_THUMB = {
  aiImageAd: cdnUrl("/zeely/explore-all/ai-image-ad.png"),
  ugcStyleVideo: cdnUrl("/UGC-placeholder.jpg"),
  avatarVideo: `${R2_TOP_TOOLS}/avatar-video-ad.png`,
  imageCloner: cdnUrl("/zeely/explore-all/image-cloner.png"),
  animateImage: `${R2_TOP_TOOLS}/animate-image.png`,
  templateAd: cdnUrl("/zeely/explore-all/template-ad.png"),
  talkingAvatar: `${R2_TOP_TOOLS}/talking-avatar.png`,
  exploreAll: `${R2_TOP_TOOLS}/explore-all.webp`,
  customAvatar: cdnUrl("/zeely/explore-all/custom-avatar.png"),
  metaCampaign: cdnUrl("/zeely/explore-all/meta-campaign.png"),
  socialPosting: cdnUrl("/zeely/explore-all/social-posting.png"),
  adClone: cdnUrl("/adclone-placeholder.jpg"),
  useTemplates: cdnUrl("/template-placeholder.jpg"),
} as const;

export const EXPLORE_ALL_TOP_TOOL_THUMBNAIL = EXPLORE_THUMB.exploreAll;

/**
 * Catalog ordered to match Zeely's "All" tab exactly:
 *   1. AI image ad           NEW
 *   2. Custom UGC videos     NEW
 *   3. Avatar video ad
 *   4. Image cloner
 *   5. Animate an image
 *   6. Template ad
 *   7. Talking avatar
 *   8. Custom avatar         NEW
 *   9. Meta campaign
 *  10. Social posting        NEW
 *
 * WiserStudio-only extras (UGC Factory, Cinematic, Music library,
 * TTS video ad) live at the bottom and are flagged
 * `hiddenInExploreModal: true` so the modal renders Zeely-perfect
 * while the routes stay reachable directly.
 */
export const ZEELY_TOOLS_CATALOG: readonly ZeelyToolEntry[] = [
  {
    id: "ai-image-ad",
    title: "AI image ad",
    description: "Create static ads optimized for performance in your brand style",
    thumbnail: EXPLORE_THUMB.aiImageAd,
    badge: "NEW",
    category: "image-ads",
    route: "/static-ads",
  },
  {
    id: "ad-clone",
    title: "Clone winning ads",
    description:
      "Find proven ads, rebuild them, then refine every scene with agents.",
    thumbnail: EXPLORE_THUMB.adClone,
    badge: "NEW",
    category: "video-ads",
    route: "/video-ads/adclone",
    isTopTool: true,
    skipAssetPicker: true,
  },
  {
    id: "ugc-style-video",
    title: "Custom UGC videos",
    description:
      "Create product-led UGC in more styles, angles, and creator formats.",
    thumbnail: EXPLORE_THUMB.ugcStyleVideo,
    badge: "NEW",
    category: "video-ads",
    route: "/video-ads/ugc-videos",
    isTopTool: true,
    skipAssetPicker: true,
  },
  {
    id: "use-templates",
    title: "Use proven templates",
    description:
      "Launch polished unboxings, demos, and product reveals fast.",
    thumbnail: EXPLORE_THUMB.useTemplates,
    badge: null,
    category: "video-ads",
    route: "/video-ads/templates",
    isTopTool: true,
    skipAssetPicker: true,
    hiddenInExploreModal: true,
  },
  {
    id: "avatar-video-ad",
    title: "Avatar video ad",
    description: "Create a 30-sec, fully edited ad with an avatar and built-in motion",
    thumbnail: EXPLORE_THUMB.avatarVideo,
    badge: null,
    category: "video-ads",
    route: "/video-ads/quick-check-in",
  },
  {
    id: "image-cloner",
    title: "Image cloner",
    description: "Create short ads with dynamic transitions built for performance",
    thumbnail: EXPLORE_THUMB.imageCloner,
    badge: null,
    category: "image-ads",
    route: "/static-ads",
  },
  {
    id: "animate-an-image",
    title: "Animate an image",
    description: "Create a start image and turn it into a motion video",
    thumbnail: EXPLORE_THUMB.animateImage,
    badge: null,
    category: "video-ads",
    route: "/video-ads/animate",
  },
  {
    id: "template-ad",
    title: "Template ad",
    description: "Start with a ready-made template and customize it to your brand",
    thumbnail: EXPLORE_THUMB.templateAd,
    badge: null,
    category: "image-ads",
    route: "/static-ads",
  },
  {
    id: "talking-avatar",
    title: "Talking avatar",
    description: "Create up to a 2-min avatar video, unedited & ready to use anywhere",
    thumbnail: EXPLORE_THUMB.talkingAvatar,
    badge: null,
    category: "video-ads",
    route: "/video-ads/talking-avatar/avatar",
    skipAssetPicker: true,
  },
  {
    id: "custom-avatar",
    title: "Custom avatar",
    description: "Create custom UGC-style avatars for your content",
    thumbnail: EXPLORE_THUMB.customAvatar,
    badge: "NEW",
    category: "avatars",
    route: "/video-ads/avatars?createAvatar=1",
  },
  {
    id: "meta-campaign",
    title: "Meta campaign",
    description: "Launch Meta campaigns to drive conversions, leads, and sales",
    thumbnail: EXPLORE_THUMB.metaCampaign,
    badge: null,
    category: "promotion",
    route: "/paid-campaigns",
  },
  {
    id: "social-posting",
    title: "Social posting",
    description: "Schedule posts for TikTok and IG to reach and engage your audience",
    thumbnail: EXPLORE_THUMB.socialPosting,
    badge: "NEW",
    category: "promotion",
    route: "/organic-campaigns",
  },

  // ── WiserStudio-only extras (hidden from the Explore-all modal for
  //    strict Zeely parity; still reachable via their direct routes). ───
  {
    id: "ugc-factory",
    title: "UGC Factory",
    description: "Generate N UGC variants in one click — fan out across hooks and angles",
    thumbnail: unsplash("1531746790731-6c087fecd65a", THUMB.topTool),
    badge: "NEW",
    category: "video-ads",
    route: "/video-ads/ugc/factory",
    hiddenInExploreModal: true,
  },
  {
    id: "cinematic-video",
    title: "Cinematic video",
    description: "Describe a scene and get an 8-second cinematic clip with ambient audio",
    thumbnail: unsplash("1500382017468-9049fed747ef", THUMB.topTool),
    badge: "NEW",
    category: "video-ads",
    route: "/video-ads/cinematic",
    skipAssetPicker: true,
    hiddenInExploreModal: true,
  },
  {
    id: "music-library",
    title: "Music library",
    description: "Royalty-free stock tracks to score your next ad",
    thumbnail: unsplash("1505740420928-5e560c06d30e", THUMB.topTool),
    badge: "NEW",
    category: "video-ads",
    route: "/video-ads/music",
    skipAssetPicker: true,
    hiddenInExploreModal: true,
  },
  {
    id: "tts-video-ad",
    title: "TTS video ad",
    description: "Narrate a still image with an AI voice — fastest path from script to ad",
    thumbnail: unsplash("1590602847861-f357a9332bbc", THUMB.topTool),
    badge: "NEW",
    category: "video-ads",
    route: "/video-ads/tts-video",
    hiddenInExploreModal: true,
  },
];

/** Top-tools row on the /video-ads landing page (cards 1–4). */
export const TOP_TOOLS: readonly ZeelyToolEntry[] = ZEELY_TOOLS_CATALOG.filter(
  (entry) => entry.isTopTool === true,
);

/** Filter helper for the "Explore all tools" modal tabs. */
export function getToolsForTab(
  tab: "all" | "new" | "image-ads" | "video-ads" | "avatars" | "promotion",
): readonly ZeelyToolEntry[] {
  const visible = ZEELY_TOOLS_CATALOG.filter((entry) => entry.hiddenInExploreModal !== true);
  if (tab === "all") return visible;
  if (tab === "new") return visible.filter((entry) => entry.badge === "NEW");
  return visible.filter((entry) => entry.category === tab);
}
